/**
 * Viewport: maps image pixel space <-> screen CSS pixel space.
 * screen = image * scale + offset. Pure math; no DOM.
 */
import type { Vec } from '../../shared/doc.ts';
import { clamp, type Rect } from './geom.ts';

export interface View {
  scale: number;
  tx: number;
  ty: number;
}

export interface ViewportLimits {
  minScale: number;
  maxScale: number;
}

export function toScreen(v: View, p: Vec): Vec {
  return { x: p.x * v.scale + v.tx, y: p.y * v.scale + v.ty };
}
export function toImage(v: View, p: Vec): Vec {
  return { x: (p.x - v.tx) / v.scale, y: (p.y - v.ty) / v.scale };
}

/**
 * The margin a plan is fitted with (CSS px): none — fitted, it meets the window's edges at whichever
 * pair it reaches first, the controls floating over it (Adam, 2026-10-29: "to the edge of the browser window").
 */
export const FIT_PAD = 0;

/** Fit the whole image inside a screen rect with padding (CSS px). */
export function fitView(imageW: number, imageH: number, screen: Rect, pad = 16): View {
  const sw = Math.max(1, screen.w - 2 * pad);
  const sh = Math.max(1, screen.h - 2 * pad);
  const scale = Math.min(sw / imageW, sh / imageH);
  const tx = screen.x + (screen.w - imageW * scale) / 2;
  const ty = screen.y + (screen.h - imageH * scale) / 2;
  return { scale, tx, ty };
}

/** Zoom by a factor around a screen-space pivot. */
export function zoomAround(v: View, factor: number, pivot: Vec, limits: ViewportLimits): View {
  const scale = clamp(v.scale * factor, limits.minScale, limits.maxScale);
  const f = scale / v.scale;
  return { scale, tx: pivot.x - (pivot.x - v.tx) * f, ty: pivot.y - (pivot.y - v.ty) * f };
}

export function pan(v: View, dx: number, dy: number): View {
  return { scale: v.scale, tx: v.tx + dx, ty: v.ty + dy };
}

/**
 * Two-finger transform: given previous & current positions of two pointers,
 * return the view that keeps the image under the fingers (pan + pinch).
 */
export function pinchView(v: View, a0: Vec, b0: Vec, a1: Vec, b1: Vec, limits: ViewportLimits): View {
  const d0 = Math.hypot(b0.x - a0.x, b0.y - a0.y) || 1;
  const d1 = Math.hypot(b1.x - a1.x, b1.y - a1.y) || 1;
  const mid0 = { x: (a0.x + b0.x) / 2, y: (a0.y + b0.y) / 2 };
  const mid1 = { x: (a1.x + b1.x) / 2, y: (a1.y + b1.y) / 2 };
  const factor = d1 / d0;
  const scale = clamp(v.scale * factor, limits.minScale, limits.maxScale);
  const f = scale / v.scale;
  // Image point under mid0 must end up under mid1.
  const tx = mid1.x - (mid0.x - v.tx) * f;
  const ty = mid1.y - (mid0.y - v.ty) * f;
  return { scale, tx, ty };
}

/**
 * Keep the image from being pushed entirely off-screen: at least `keep` CSS px
 * of the image stay visible on each axis.
 */
export function constrain(v: View, imageW: number, imageH: number, screen: Rect, keep = 80): View {
  const w = imageW * v.scale;
  const h = imageH * v.scale;
  const minTx = screen.x + keep - w;
  const maxTx = screen.x + screen.w - keep;
  const minTy = screen.y + keep - h;
  const maxTy = screen.y + screen.h - keep;
  return { scale: v.scale, tx: clamp(v.tx, Math.min(minTx, maxTx), Math.max(minTx, maxTx)), ty: clamp(v.ty, Math.min(minTy, maxTy), Math.max(minTy, maxTy)) };
}

export function viewEquals(a: View, b: View): boolean {
  return a.scale === b.scale && a.tx === b.tx && a.ty === b.ty;
}
